import { Questionnaire } from "../types/fhir";
import { apiClientService, ApiGateWayResponse } from "./apiClientService";
import { QuestionnaireAndResponseDto, QuestionnaireIdAndTitle } from "../types/QuestionnaireItem";
import { emptyPropertyReplacer } from "../helpers/emptyPropertyReplacer";
import { SimpleQuestionnaire } from "../types/SimpleQuestionnaire";

const apiGatewayInstance = apiClientService.apiGateway;

const getAllForOverview = async (): Promise<SimpleQuestionnaire[]> => {
  const httpResponse = await apiGatewayInstance.get(`api/Questionnaire/GetAllQuestionnaires`);

  const response = httpResponse.data as ApiGateWayResponse<SimpleQuestionnaire[]>;
  if (response.error || response.data === undefined) {
    throw new Error(response.error?.message ?? "An error occurred getting Questionnaires for overview");
  }

  return response.data;
};

const getOne = async (id: string): Promise<Questionnaire | undefined> => {
  const httpResponse = await apiGatewayInstance.get(`api/Questionnaire/GetQuestionnaireById/${id}`);
  if (httpResponse.status !== 200) {
    throw new Error("An error occurred getting Questionnaire by ID");
  }
  return httpResponse.data as Questionnaire;
};

const getQuestionnairesUsingMedia = async (mediaId: string): Promise<QuestionnaireIdAndTitle[] | undefined> => {
  const httpResponse = await apiGatewayInstance.get(
    `api/Questionnaire/GetQuestionnairesByMediaId?mediaId=${mediaId}`
  );
  if (httpResponse.status !== 200) {
    throw new Error("An error occurred getting Questionnaires by media ID");
  }
  return httpResponse.data as QuestionnaireIdAndTitle[];
};

const getByTitle = async (title: string, exact = false): Promise<Questionnaire[] | undefined> => {
  try {
    const response = await apiGatewayInstance.get<Questionnaire[]>(
      `api/Questionnaire/GetQuestionnairesByTitle?title=${title}`
    );
    if (response.status !== 200) {
      console.error(`Error fetching questionnaires by title: ${response.statusText}`);
      return undefined;
    }

    if (exact) {
      return response.data?.filter((q) => q.title === title);
    }
    return response.data;
  } catch (error) {
    console.error("Error fetching questionnaires by title:", error);
    return undefined;
  }
};

const getByUuid = async (uuid: string | undefined): Promise<Questionnaire | undefined> => {
  if (!uuid) return undefined;
  const httpResponse = await apiGatewayInstance.get(`api/Questionnaire/GetQuestionnaire?questionnaireUuid=${uuid}`);
  if (httpResponse.status === 417) {
    return undefined; // No questionnaire found with the given UUID
  }
  const response = httpResponse.data as ApiGateWayResponse<QuestionnaireAndResponseDto>;
  if (response.error || response.data === undefined) {
    throw new Error(response.error?.message ?? "An error occurred getting Questionnaire by UUID");
  }
  return response.data.questionnaire;
};

const removeOne = async (id: string): Promise<number> => {
  const response = await apiGatewayInstance.delete(`api/Questionnaire/DeleteQuestionnaire/${id}`);
  return response.status;
};

const updateQuestionnaire = async (
  questionnaire: Questionnaire,
  shouldBumpVersion: boolean,
  majorChange?: boolean
): Promise<Questionnaire> => {
  const httpResponse = await apiGatewayInstance.put(
    `api/Questionnaire/UpdateQuestionnaire`,
    JSON.stringify(
      {
        questionnaire,
        shouldBumpVersion: shouldBumpVersion,
        majorChange: majorChange ?? false,
      },
      emptyPropertyReplacer
    )
  );

  const response = httpResponse.data as ApiGateWayResponse<Questionnaire>;
  if (response.error || response.data === undefined) {
    throw new Error(response.error?.message ?? "An error occurred updating the Questionnaire");
  }

  return response.data;
};

const questionnaireService = {
  getOne,
  getByTitle,
  getByUuid,
  removeOne,
  getAllForOverview,
  getQuestionnairesUsingMedia,
  updateQuestionnaire,
};

export default questionnaireService;
